import {
    IBalusterGeometry,
    IRailingGeometry,
    IRoomGeometry,
    IStairCalculationResult,
    IStairConfig,
    IStairGeometry,
    IStepGeometry,
    IStringerGeometry,
    IValidationItem,
} from '@stair-craft/common/util';

import { IStairNorms, RESIDENTIAL_NORMS } from '../norms';
import { validate, validateRailing, withHeadroomHint } from '../validator';
import { evenPitchRailing } from './shared-baluster-count/baluster-count';
import { IHeadroomResult, calculateHeadroom, lowHeadroomSteps } from './shared-calculate-headroom/calculate-headroom';
import { capRailingPosts } from './shared-post-caps/post-caps';
import { settleStairRailings } from './shared-settle-railings/settle-railings';
import { stairPlacement } from './shared-stair-placement/stair-placement';
import { ISpiralLayout } from './spiral-calculate-layout/calculate-layout';
import { calculateMaterialVolume } from './spiral-calculate-material-volume/calculate-material-volume';
import { generateExitSteps } from './spiral-generate-exit-steps/generate-exit-steps';
import { generatePole } from './spiral-generate-pole/generate-pole';
import { generateSpiralRailings } from './spiral-generate-spiral-railings/generate-spiral-railings';
import { generateSteps } from './spiral-generate-steps/generate-steps';
import { generateUpperFloorRailing } from './spiral-generate-upper-floor-railing/generate-upper-floor-railing';
import { pickAutoLayout } from './spiral-pick-auto-layout/pick-auto-layout';

// ─── Main Calculator ─────────────────────────────────────────

/** Where a landing rail ends at the upper ring, the ring's end post serves both. */
function dropPostsSharedWithRing(stairRailings: readonly IRailingGeometry[], upperRailings: readonly IRailingGeometry[]): void {
    const upperPosts: IBalusterGeometry[] = upperRailings.flatMap((railing: IRailingGeometry) => railing.posts);
    const isShared: (post: IBalusterGeometry) => boolean = (post: IBalusterGeometry): boolean =>
        upperPosts.some(
            (upper: IBalusterGeometry) =>
                Math.hypot(upper.position.x - post.position.x, upper.position.z - post.position.z) < 1 &&
                Math.abs(upper.position.y - post.position.y) < 1
        );
    for (const railing of stairRailings) {
        railing.posts = railing.posts.filter((post: IBalusterGeometry) => !isShared(post));
    }
}

export function calculateSpiral(config: IStairConfig): IStairCalculationResult {
    const norms: IStairNorms = RESIDENTIAL_NORMS;

    // ─── Layout ────────────────────────────────────────────
    // No rotation in the config: the riser count and the step angle are picked to stay in norm.
    const layout: ISpiralLayout = pickAutoLayout(config, norms);
    const { stepCount, riserHeight, outerRadius, treadWidth, treadDepthAtWalkingLine, walkingLineR, anglePerStepRad } = layout;

    // ─── Steps + exit step ───────────────────────────────
    const steps: IStepGeometry[] = generateSteps(layout, config);
    const exitSteps: IStepGeometry[] = generateExitSteps(layout, config);
    const allSteps: IStepGeometry[] = [...steps, ...exitSteps];

    // ─── Central Pole ──────────────────────────────────────
    const pole: IStringerGeometry = generatePole(layout);

    // ─── Railings ──────────────────────────────────────────
    // One baluster pitch along the helix and the level rails: the arc of a tread at the rail line
    // cut into gaps within the norm.
    const going: number = (outerRadius - config.railing.postWidth / 2) * anglePerStepRad;
    const railed: IStairConfig = { ...config, railing: evenPitchRailing(config.railing, going) };
    const stairRailings: IRailingGeometry[] = config.railing.enabled ? generateSpiralRailings(steps, layout, railed) : [];
    const upperRailings: IRailingGeometry[] =
        config.railing.enabled && config.railing.upperEnabled ? generateUpperFloorRailing(railed, layout) : [];
    dropPostsSharedWithRing(stairRailings, upperRailings);
    settleStairRailings(stairRailings, allSteps, config.stringerType, config.railing.balusterSpacing);
    const railings: IRailingGeometry[] = [...stairRailings, ...upperRailings];
    capRailingPosts(railings);

    // ─── Bounding box ──────────────────────────────────────
    const diameter: number = outerRadius * 2;
    const geometry: IStairGeometry = {
        railings,
        steps: allSteps,
        stringers: [pole],
        landings: [],
        risers: config.risersEnabled,
        boundingBox: {
            min: { x: 0, y: 0, z: 0 },
            max: { x: diameter, y: config.floorHeight + config.floorThickness, z: diameter },
        },
    };

    // ─── Derived values ────────────────────────────────────
    const runPerStepAtWalkingLine: number = walkingLineR * anglePerStepRad;
    const stairAngle: number = Math.atan2(riserHeight, runPerStepAtWalkingLine) * (180 / Math.PI);
    const totalRunAtWalkingLine: number = runPerStepAtWalkingLine * (stepCount - 1);
    const totalFloorHeight: number = config.floorHeight + config.floorThickness;
    const totalMarchLength: number = Math.sqrt(totalFloorHeight ** 2 + totalRunAtWalkingLine ** 2);
    // The treads above — the next turn and the exit platform — are the overhead; the round opening clears every tread.
    const headroomResult: IHeadroomResult = calculateHeadroom(allSteps, config, stairPlacement(config, config, diameter), false);
    const headroom: number = headroomResult.headroom;
    const blondelValue: number = 2 * riserHeight + treadDepthAtWalkingLine;

    // ─── Material estimation ───────────────────────────────
    const { woodVolumeM3, estimatedWeightKg, estimatedCostUsd } = calculateMaterialVolume(
        allSteps,
        stepCount,
        riserHeight,
        layout,
        railings,
        config
    );

    // ─── Room geometry ─────────────────────────────────────
    const room: IRoomGeometry = {
        roomLength: config.roomLength,
        roomWidth: config.roomWidth,
        floorHeight: config.floorHeight,
        floorThickness: config.floorThickness,
        openingLength: config.openingLength,
        openingWidth: config.openingWidth,
        openingX: config.openingX,
        openingZ: config.openingZ,
        openingCircular: true,
        openingExitAngle: ((layout.totalRotationDeg * Math.PI) / 180) * (config.turnDirection === 'right' ? 1 : -1),
    };

    // ─── Validation ────────────────────────────────────────
    const validation: IValidationItem[] = withHeadroomHint(
        validate(riserHeight, treadDepthAtWalkingLine, stairAngle, headroom, treadWidth),
        true
    );
    validation.push(...validateRailing(config.railing));

    // СП 54.13330 (former СНиП 2.08.01-89): winder depth is measured norms.winderMeasureOffset from the narrow edge.
    // The narrow edge sits at radius layout.innerRadius (POLE_RADIUS + WINDER_INNER_OFFSET).
    const measureRadius: number = layout.innerRadius + norms.winderMeasureOffset;
    const innerDepth: number = Math.round(2 * measureRadius * Math.sin(anglePerStepRad / 2));
    const winderInnerDepthLabel: string = $localize`:@@valWinderInnerDepth:Глубина забежной у узкого края`;
    const mmUnit: string = $localize`:@@unitMm:мм`;
    const minUnit: string = $localize`:@@unitMin:мин`;
    validation.push({
        code: 'WINDER_INNER_DEPTH',
        severity: innerDepth >= norms.winderMinInnerDepth ? 'ok' : 'error',
        value: innerDepth,
        min: norms.winderMinInnerDepth,
        max: null,
        message: `${winderInnerDepthLabel}: ${String(innerDepth)}${mmUnit} (${minUnit} ${String(norms.winderMinInnerDepth)})`,
    });

    const isValid: boolean = validation.every((v: IValidationItem) => v.severity !== 'error');

    // ─── Step warnings (red highlight) ─────────────────────
    // Only the treads that lack headroom.
    const warningStepIndices: number[] = lowHeadroomSteps(headroomResult, norms.headroomMin);

    return {
        riserHeight: Math.round(riserHeight * 10) / 10,
        treadDepth: Math.round(treadDepthAtWalkingLine * 10) / 10,
        stairAngle: Math.round(stairAngle),
        totalRun: Math.round(diameter),
        headroom: Math.round(headroom),
        blondelValue: Math.round(blondelValue),
        totalMarchLength: Math.round(totalMarchLength),
        woodVolumeM3: Math.round(woodVolumeM3 * 1000) / 1000,
        estimatedWeightKg: Math.round(estimatedWeightKg),
        estimatedCostUsd: Math.round(estimatedCostUsd),
        placementValid: warningStepIndices.length === 0,
        stepCount,
        room,
        geometry,
        validation,
        isValid,
        warningStepIndices,
    };
}
